import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type {
  TierChange,
  TierOrderBreak,
} from '../../shared/reorder-plan'

export interface ReorderConfirmDialogProps {
  open: boolean
  displayName: string
  direction: 'earlier' | 'later'
  tierChanges: TierChange[]
  /** Entries that would sit behind something unlocking later. Reads as a mistake. */
  orderBreaks?: TierOrderBreak[]
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}

export default function ReorderConfirmDialog({
  open,
  displayName,
  direction,
  tierChanges,
  orderBreaks = [],
  busy = false,
  onCancel,
  onConfirm,
}: ReorderConfirmDialogProps) {
  const { formatMessage } = useIntl()
  const tierLabel = (tier: number | null) =>
    tier === null
      ? formatMessage({ id: 'avatarStudio.reorder.noTier' })
      : formatMessage({ id: 'avatarStudio.option.tierBadge' }, { tier })

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth={true}>
      <DialogTitle>
        {formatMessage(
          {
            id:
              direction === 'earlier'
                ? 'avatarStudio.reorder.titleEarlier'
                : 'avatarStudio.reorder.titleLater',
          },
          { name: displayName },
        )}
      </DialogTitle>
      <DialogContent>
        {tierChanges.length > 0 && (
          <>
            <DialogContentText>
              {formatMessage({ id: 'avatarStudio.reorder.positional' })}
            </DialogContentText>
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {tierChanges.map((change) => (
                <Typography key={change.slug} variant="body2">
                  {formatMessage(
                    { id: 'avatarStudio.reorder.change' },
                    {
                      name: change.displayName,
                      before: tierLabel(change.before),
                      after: tierLabel(change.after),
                    },
                  )}
                </Typography>
              ))}
            </Stack>
          </>
        )}

        {orderBreaks.length > 0 && (
          <Stack
            sx={{ mt: tierChanges.length > 0 ? 2 : 0 }}
            data-testid="order-break"
          >
            <DialogContentText>
              {formatMessage({ id: 'avatarStudio.reorder.outOfTierOrder' })}
            </DialogContentText>
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {orderBreaks.map((b) => (
                <Typography key={b.key} variant="body2" translate="no">
                  {formatMessage(
                    { id: 'avatarStudio.reorder.behind' },
                    {
                      name: b.displayName,
                      tier: tierLabel(b.tier),
                      other: b.afterKey,
                      otherTier: tierLabel(b.afterTier),
                    },
                  )}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {formatMessage({ id: 'avatarStudio.editor.cancel' })}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={busy}>
          {formatMessage({ id: 'avatarStudio.reorder.confirm' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
